import { useEffect, useState } from 'react'
import Money, { MoneyRoundingStrategy } from '@/common/models/money'
import { useFetchBalance, useFetchPayId } from '@/wallet/walletQueries'
import { Button, H2 } from '@/common/ui'
import { ValuesGrid } from '@/common/ui/ValuesGrid'
import { ValuesGridItem } from '@/common/ui/ValuesGridItem'
import WithdrawDialogContainer from './WithdrawDialogContainer'
import useKycStore from '@/kyc/kycStore'
import { seconds } from '@/common/utils/utils'
import { analytics } from '@/common/analytics/analytics'
import AddFundsModal from './AddFundsModal'
import useAccountStore from '@/account/accountStore'
import { ANALYTICS_EVENTS } from '@/common/analytics/events'

export default function FundsAndBalances() {
    const activeAccountId = useAccountStore((s) => s.activeAccountId)
    const [isFundDetailsOpen, setIsFundDetailsOpen] = useState(false)
    const [openWithdrawDialog, setOpenWithdrawDialog] = useState(false)
    const [isPayIdCopied, setIsPayIdCopied] = useState(false)
    const [reservedBalance, setReservedBalance] = useState(0)
    const { kycComplete } = useKycStore()
    const { data: payIdDetails } = useFetchPayId(activeAccountId, kycComplete)
    const { data: balance } = useFetchBalance(activeAccountId, {
        enabled: kycComplete,
        refetchInterval: seconds(10),
    })

    const openFundDetails = () => {
        setIsFundDetailsOpen(true)
        analytics.trackWithDefaults(ANALYTICS_EVENTS.WALLET.ADD_FUNDS_BUTTON_CLICKED, {
            source_page: 'wallet',
        })
    }

    const closeFundDetails = () => {
        setIsFundDetailsOpen(false)
    }

    const handleOpenWithdrawalModal = () => {
        setOpenWithdrawDialog(true)
        analytics.track('WithdrawFundsButton Clicked')
    }

    useEffect(() => {
        if (!isPayIdCopied) return
        setTimeout(() => {
            setIsPayIdCopied(false)
        }, 2000)
    }, [isPayIdCopied])

    useEffect(() => {
        if (balance) {
            setReservedBalance(balance.totalBalance - balance.availableBalance)
        }
    }, [balance])

    return (
        <>
            <section>
                <div className="flex space-x-5">
                    <H2 className="mb-6">Funds &amp; Balances</H2>
                    <Button disabled={!payIdDetails} variant="secondary" onClick={openFundDetails}>
                        Add Funds
                    </Button>
                    <Button
                        disabled={!(balance && balance.availableBalance > 0)}
                        variant="outline"
                        onClick={handleOpenWithdrawalModal}
                    >
                        Withdraw Funds
                    </Button>
                </div>
                <ValuesGrid>
                    <ValuesGridItem label="Available Balance" value={`${Money.of(balance?.availableBalance ?? 0)}`} />
                    <ValuesGridItem label="Total Balance" value={`${Money.of(balance?.totalBalance ?? 0)}`} />
                    <ValuesGridItem
                        label="Settlements Expected"
                        value={`${Money.of(balance?.settlemenetBalance ?? 0)}`}
                    />
                </ValuesGrid>
                {reservedBalance > 0 && (
                    <p className="mt-6">
                        <span className="font-bold">{`${Money.of(reservedBalance, MoneyRoundingStrategy.CEILING)}`}</span>{' '}
                        is currently reserved for your open orders.
                    </p>
                )}
            </section>

            {isFundDetailsOpen && payIdDetails && (
                <AddFundsModal open={isFundDetailsOpen} onClose={closeFundDetails} payIdDetails={payIdDetails} />
            )}

            {openWithdrawDialog && (
                <WithdrawDialogContainer
                    showWithdrawDialog={openWithdrawDialog}
                    onClose={() => setOpenWithdrawDialog(false)}
                    availableBalance={balance?.availableBalance || 0}
                />
            )}
        </>
    )
}
